import { useEffect, useRef, useState } from "react";
import { api, type Item, type Link, type TripFile, type TripView } from "./api.ts";
import { AttachFile, FileList } from "./Files.tsx";
import { useFocusAfter } from "./focusAfter.ts";
import { ItemLinks } from "./ItemLinks.tsx";
import { LinkError, LinkForm, useLinkChange } from "./LinkEdits.tsx";
import { addressOf, type LeaveOut, type LinkRow, linkRows } from "./links.ts";
import { useReadOnly } from "./readOnly.ts";
import { Button } from "./souvenir.ts";
import { ItemIcon } from "./ui.tsx";

// A card's Files and links (DESIGN.md, "A card reads in sections", and Views, the web's own edits; features/links.md,
// "Add, edit and remove every link on the web"): its links as pills, quiet, each opening the link; its files; then
// Attach a file, Add a photo, Add a link and, with links to change, Edit links. Add a link takes a pasted address
// and a note if I like, saved as a link about this item (its preview fetched as on Ideas). Edit links turns the pills
// into rows, each with Edit and Remove: one of its own (its website, booking page, email, social pages and other
// links) changes or leaves the field it's kept in; a saved post or video changes its address, its note and what
// it's about, or is removed (from every place it's about). Anyone in the trip, on a trip that can change; each one
// write signed "on the web", with Undo.

// What a card's Files and links leaves to the rest of it: its phone (At a glance calls it) and its booking page
// (under Booking). Edit links lists the booking page too: it's one of the card's links to change.
export const LEAVE_OUT: LeaveOut = { phone: true, booking: true };

export function FilesAndLinks({
  item,
  trip,
  items,
  links,
  files,
  invited,
  photos,
  onChanged,
}: {
  item: Item;
  trip: TripView;
  items: Item[];
  links: Link[];
  // Its files (its own photos show at the top of the card instead).
  files: TripFile[];
  invited: "file" | "photo" | null;
  photos: boolean;
  onChanged: () => void;
}) {
  const readOnly = useReadOnly();
  const rows = linkRows(item, links, trip.you);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const area = useRef<HTMLDivElement>(null);
  const add = useLinkChange(trip.id, onChanged);
  // Nothing left to change: out of Edit.
  useEffect(() => {
    if (rows.length === 0) setEditing(false);
  }, [rows.length]);
  // The focus on the first of these of its controls that's there, once it's drawn.
  const focus = useFocusAfter(area);
  return (
    <div className="tm-files-links" ref={area}>
      {editing && !readOnly ? (
        <ul className="tm-link-rows" aria-label="Links">
          {rows.map((r) => (
            <EditRow
              key={r.key}
              row={r}
              item={item}
              trip={trip}
              items={items}
              onChanged={onChanged}
              onGone={() => focus('[data-edit="links"]', '[data-edit="link-add"]')}
            />
          ))}
        </ul>
      ) : (
        <ItemLinks item={item} saved={links} label="Links" leaveOut={LEAVE_OUT} />
      )}
      <FileList files={files} trip={trip.id} canRemove onChanged={onChanged} />
      {adding && !readOnly && (
        <LinkForm
          label="Add a link"
          ariaLabel="Add a link"
          note
          busy={add.busy}
          offline={add.offline}
          error={add.error}
          onSave={async ({ url, note }) => {
            const ok = await add.run(
              () => api.saveLink(trip.id, { url, ...(note ? { note } : {}), items: [item.id] }),
              `Added the link to ${item.title}`,
            );
            if (!ok) return;
            setAdding(false);
            focus('[data-edit="link-add"]');
          }}
          onCancel={() => {
            setAdding(false);
            add.setError(null);
            focus('[data-edit="link-add"]');
          }}
        />
      )}
      <AttachFile
        key={item.id}
        trip={trip.id}
        on={item.id}
        invited={invited}
        photos={photos}
        hidden={adding}
        onChanged={onChanged}
        more={
          <>
            <Button
              variant="quiet"
              size="sm"
              type="button"
              icon="plus"
              data-edit="link-add"
              onClick={() => setAdding(true)}
            >
              Add a link
            </Button>
            {rows.length > 0 && (
              <Button
                variant="quiet"
                size="sm"
                type="button"
                icon={editing ? "check" : "note"}
                data-edit="links"
                aria-pressed={editing}
                onClick={() => setEditing(!editing)}
              >
                {editing ? "Done" : "Edit links"}
              </Button>
            )}
          </>
        }
      />
    </div>
  );
}

// An address as a row says it under its words: no "https://" or "www.", no slash at its end.
const shortAddress = (url: string) =>
  addressOf(url)
    .replace(/^https?:\/\/(www\.)?/i, "")
    .replace(/\/$/, "");

// One of a card's links in Edit (one pill: its address everywhere it's kept on the card): what it is, its address,
// and Edit and Remove. Edit turns it into its form in place: its address, and for a saved post its note and what it's
// about too. A saved post alone is changed as Ideas changes one (`api.editLink`, and Remove deletes it); an address
// the card keeps in a field of its own changes, or comes off, everywhere it's kept, saved posts at that address
// included, as one change (`api.editCardLink`).
function EditRow({
  row,
  item,
  trip,
  items,
  onChanged,
  onGone,
}: {
  row: LinkRow;
  item: Item;
  trip: TripView;
  items: Item[];
  onChanged: () => void;
  // Removed: the focus goes on (to Done, or with none left, to Add a link).
  onGone: () => void;
}) {
  const change = useLinkChange(trip.id, onChanged);
  const [open, setOpen] = useState(false);
  const line = useRef<HTMLLIElement>(null);
  // Closed: the focus back on its Edit.
  const focus = useFocusAfter(line);
  const close = () => {
    setOpen(false);
    change.setError(null);
    focus('[data-link-action="edit"]');
  };
  const email = row.own.includes("email");
  // A saved post alone, not kept in a field of the card's own.
  const [post] = row.saved;
  const alone = row.own.length === 0 && row.saved.length === 1 && post ? post : null;
  // Its note and what it's about, where it's one saved post.
  const one = row.saved.length === 1 && post ? post : null;
  const saved = Object.fromEntries(row.saved.map((l) => [l.id, l.version]));
  const everywhere = (to: string | null, more: { note?: string | null; items?: string[] } = {}) =>
    api.editCardLink(trip.id, item.id, { version: item.version, from: row.url, to, saved, ...more });
  const remove = async () => {
    const ok = await change.run(
      () => (alone ? api.removeLink(trip.id, alone.id) : everywhere(null)),
      "Removed the link",
    );
    if (ok) onGone();
  };
  if (open)
    return (
      <li className="tm-link-row is-editing" ref={line}>
        <LinkForm
          label={email ? "Email address" : "Address"}
          ariaLabel={`Edit the link ${row.label}`}
          email={email}
          start={{ url: addressOf(row.url), note: one?.note ?? null, items: one?.items ?? [] }}
          note={one !== null}
          {...(one ? { about: items } : {})}
          busy={change.busy}
          offline={change.offline}
          error={change.error}
          onSave={async ({ url, note, items: about }) => {
            const more = one
              ? {
                  ...(note !== one.note ? { note } : {}),
                  ...(JSON.stringify(about) !== JSON.stringify(one.items) ? { items: about } : {}),
                }
              : {};
            const ok = await change.run(
              () =>
                alone
                  ? api.editLink(trip.id, alone.id, {
                      version: alone.version,
                      ...(url !== alone.url ? { url } : {}),
                      ...more,
                    })
                  : everywhere(email ? `mailto:${url}` : url, more),
              "Changed the link",
            );
            if (ok) close();
          }}
          onCancel={close}
        />
      </li>
    );
  const web = /^https?:/.test(row.url);
  const note = row.saved.find((l) => l.note)?.note;
  return (
    <li className="tm-link-row" ref={line}>
      <a className="tm-link-row-open" href={row.url} {...(web ? { target: "_blank", rel: "noreferrer noopener" } : {})}>
        {row.thumbnail ? (
          <img className="tm-link-row-thumb" src={row.thumbnail} alt="" referrerPolicy="no-referrer" />
        ) : (
          <span className="tm-link-row-icon" aria-hidden="true">
            <ItemIcon name={email ? "mail" : "link"} size={16} />
          </span>
        )}
        <span className="tm-link-row-text">
          <span className="callout tm-link-row-label">{row.label}</span>
          <span className="caption tm-muted tm-link-row-what">
            {email ? row.what : `${row.what} · ${shortAddress(row.url)}`}
          </span>
          {note && <span className="caption tm-link-row-note">{note}</span>}
        </span>
      </a>
      <span className="tm-link-row-actions">
        <Button
          variant="quiet"
          size="sm"
          type="button"
          data-link-action="edit"
          aria-label={`Edit the link ${row.label}`}
          disabled={change.busy}
          onClick={() => setOpen(true)}
        >
          Edit
        </Button>
        <Button
          variant="quiet"
          size="sm"
          type="button"
          aria-label={`Remove the link ${row.label}`}
          disabled={change.busy || change.offline}
          onClick={remove}
        >
          {change.busy ? "Removing…" : "Remove"}
        </Button>
      </span>
      <LinkError error={change.error} />
    </li>
  );
}
